import { CompanyService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useCompanyActivityStore } from './companyActivityStore';
import { useCompanyLinkRulesStore } from './companyLinkRulesStore';
import type {
  CompanyActivityLink,
  CompanyLink,
  CreateCompanyLinkRequest,
  UpdateCompanyLinkRequest,
} from '@beeboard/common';

export type CompanyLinkStoreState = {
  companyLinks: CompanyLink[];
};

export const useCompanyLinkStore = defineStore('companyLinkStore', {
  state: (): CompanyLinkStoreState => ({
    companyLinks: [] as CompanyLink[],
  }),

  getters: {
    companyLink(state) {
      return (companyLinkId: string): CompanyLink => {
        const link = state.companyLinks.find(
          (link) => link.id === companyLinkId,
        );
        if (!link) {
          throw new Error(
            `Missing companyLink ${companyLinkId} in companyLink store`,
          );
        }

        return link;
      };
    },

    hasCompanyLink(state) {
      return (companyLinkId: string): boolean => {
        return state.companyLinks.some((link) => link.id === companyLinkId);
      };
    },
  },

  actions: {
    write(link: CompanyLink) {
      const links = this.$state.companyLinks;
      const oldLink = links.find((l) => l.id === link.id);
      if (oldLink) {
        Object.assign(oldLink, link);
      } else {
        links.push(link);
      }
    },

    async loadCompanyLinks() {
      const companyLinks = await CompanyService.getCompanyLinks();
      this.$patch({ companyLinks });
    },

    async loadCompanyLink(linkId: string) {
      const link = await CompanyService.getCompanyLink(linkId);
      this.write(link);
    },

    async create(payload: CreateCompanyLinkRequest) {
      const errors =
        useCompanyLinkRulesStore().validateCreateCompanyLinkRequest(payload);
      if (errors) {
        throw errors;
      }
      const link = await CompanyService.createCompanyLink(payload);
      if (payload.activity_id) {
        const activity = useCompanyActivityStore().companyActivity(
          payload.activity_id,
        );
        if (link.link !== null) {
          activity.links.push(link as CompanyActivityLink);
        }
        await useCompanyActivityStore().loadCompanyActivity(
          payload.activity_id,
        );
      }
      this.write(link);
    },

    async update(linkId: string, payload: UpdateCompanyLinkRequest) {
      const errors =
        useCompanyLinkRulesStore().validateUpdateCompanyLinkRequest(
          payload,
          linkId,
        );
      if (errors) {
        throw errors;
      }
      const link = await CompanyService.updateCompanyLink(linkId, payload);
      this.write(link);
    },

    async delete(linkId: string) {
      const errors =
        useCompanyLinkRulesStore().validateRemoveCompanyLinkRequest(linkId);
      if (errors) {
        throw errors;
      }
      await CompanyService.deleteCompanyLink(linkId);
      const links = this.$state.companyLinks;
      const linkIndex = links.findIndex((l) => l.id === linkId);
      if (linkIndex > -1) {
        links.splice(linkIndex, 1);
      }
    },
  },
});
